import {SeverityLevel} from "@microsoft/applicationinsights-web";
import {observable, makeObservable, action, runInAction} from "mobx";
import {IAccountService} from "../../../services/accountService";
import {Village} from "../../../shared/interfaces/village";
import {
  AppAbilityType,
  buildAbilityFor
} from "../../../utils/auth/authorisation/authorisation";
import {AuthUser} from "../../../utils/auth/user";
import {ApplicationInsightsTelemetry} from "../../../utils/telemetry/applicationInsightsTelemetry";

export interface IAuthEndpoints {
  signIn: string;
  signOut: string;
}

export class AuthEndpoints implements IAuthEndpoints {
  signIn: string = "";
  signOut: string = "";
}

export interface IAccountStore {
  /** Whether the user is authenticated to access admin portal. */
  isAuthenticated: boolean;
  /** Whether the user is authorised to access the features on the admin portal. */
  isAuthorised: boolean;
  /** The villages that the user has access to. */
  villages: Village[] | null;
  abilities: AppAbilityType;
  endpoints: AuthEndpoints;
  populateAuthEndpoints: () => Promise<void>;
  authenticate: () => Promise<void>;
  invalidateAuthenticated: () => void;
}

export class AccountStore implements IAccountStore {
  isAuthenticated: boolean;
  isAuthorised: boolean;
  villages: Village[] | null;
  abilities: AppAbilityType;
  endpoints: AuthEndpoints;
  private _accountService: IAccountService;
  private readonly _getVillagesErrorMessage =
    "Failed to retrieve the villages of the user.";

  constructor(accountService: IAccountService) {
    makeObservable(this, {
      isAuthenticated: observable,
      isAuthorised: observable,
      endpoints: observable,
      populateAuthEndpoints: action,
      authenticate: action,
      invalidateAuthenticated: action,
      villages: observable,
      abilities: observable
    });
    this._accountService = accountService;
    this.isAuthenticated = false;
    this.isAuthorised = false;
    this.endpoints = new AuthEndpoints();
    this.villages = null;
    this.abilities = buildAbilityFor([], null);
  }

  populateAuthEndpoints = async () => {
    const response = await this._accountService.getAccountEndpoints();
    const data: AuthEndpoints = await response.json();
    localStorage.setItem("data", JSON.stringify(data));
    Object.assign(this.endpoints, data);
  };

  authenticate = async () => {
    const response = await this._accountService.authenticate();
    if (response.status === 200) {
      const villagesResponse = await this.getVillagesResponse();
      if (!villagesResponse) {
        throw new Error(this._getVillagesErrorMessage);
      }
      AuthUser.getInstance().setAuthUser(
        await response.json(),
        await villagesResponse.json()
      );
      runInAction(() => {
        this.isAuthenticated = true;
        this.isAuthorised = AuthUser.getInstance().IsAuthorised;
        this.villages = AuthUser.getInstance().Villages;
        this.abilities = buildAbilityFor(
          AuthUser.getInstance().Permissions,
          AuthUser.getInstance().Roles
        );
      });
    } else {
      runInAction(() => {
        this.isAuthenticated = false;
        this.isAuthorised = false;
        this.villages = null;
      });
    }
  };

  invalidateAuthenticated = () => {
    this.isAuthenticated = false;
    this.isAuthorised = false;
    this.villages = null;
  };

  private getVillagesResponse = async (): Promise<any> => {
    const villagesResponse = await this._accountService.getVillages();
    if (villagesResponse.status !== 200) {
      let detail: any = undefined;
      try {
        detail = (await villagesResponse.json()).detail;
      } catch (e) {}
      ApplicationInsightsTelemetry.getInstance().applicationInsights?.trackTrace(
        {
          message: this._getVillagesErrorMessage,
          severityLevel: SeverityLevel.Error,
          properties: {
            status: villagesResponse.status,
            detail: detail
          }
        }
      );
      return null;
    }
    return villagesResponse;
  };
}
